/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
'use client'

import { ArrowUp, Mic, MicOff, Paperclip } from 'lucide-react'
import { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { useSpeechToText } from '@/hooks/useSpeechToText'

import FileAttachmentsList from './file-attachments-list'

type Props = {
  /** Shown in place of the normal placeholder while disabled. */
  disabledMessage?: string
  fileAttachments?: File[]
  /** Locks composing while leaving the conversation readable. */
  isDisabled?: boolean
  isSubmitEnabled?: boolean
  onChangeFiles?(files: File[]): void
  onRequestFileAttachment?(): void
  onSubmit?(text: string): void
  value?: string
}

const ChatInputArea: FC<Props> = ({
  disabledMessage,
  fileAttachments = [],
  isDisabled = false,
  isSubmitEnabled = true,
  onChangeFiles = () => {},
  onRequestFileAttachment,
  onSubmit,
}) => {
  const { i18n, t } = useTranslation()
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const [text, setText] = useState('')
  const speechToText = useSpeechToText({
    getBaseText: () => text,
    lang: i18n.language,
    onTextChange: setText,
  })

  const fixHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) fixHeight()
      })
    })

    if (textareaRef.current) {
      observer.observe(textareaRef.current)
    }

    return () => {
      if (textareaRef.current) {
        observer.unobserve(textareaRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (textareaRef.current) fixHeight()
  }, [text])

  const handleSubmit = () => {
    if (!isSubmitEnabled || text.trim().length === 0) return
    if (speechToText.isListening) speechToText.stop()
    onSubmit?.(text.trim())
    setText('')
  }

  return (
    <div className="relative">
      <div
        className="z-10 max-h-[500px] gap-2 rounded-lg border bg-layer-1 dark:bg-layer-2 focus-within:border-focus focus-within:ring-1 focus-within:ring-focus"
        onClick={() => textareaRef.current?.focus()}
      >
        <div className="absolute inset-x-0 top-0 h-6 -translate-y-full bg-linear-to-b from-transparent to-slate-50" />
        <FileAttachmentsList files={fileAttachments} onFilesUpdated={onChangeFiles} />
        <form
          onSubmit={e => {
            e.preventDefault()
            handleSubmit()
          }}
        >
          <div className="flex gap-2 rounded-lg">
            <textarea
              className="w-full max-h-[400px] bg-transparent flex-1 resize-none rounded-[8px] border-0 p-4 pb-1 text-base outline-none focus:!ring-transparent focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isDisabled}
              onChange={e => setText(e.currentTarget.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSubmit()
                }
              }}
              placeholder={isDisabled ? disabledMessage : t('aiChat.placeholder')}
              ref={textareaRef}
              rows={1}
              value={text}
            />
          </div>
          <div className="flex items-center justify-between p-2.5 gap-2">
            <div className="flex gap-2 items-center">
              {onRequestFileAttachment && (
                <Button
                  disabled={isDisabled}
                  onClick={onRequestFileAttachment}
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <Paperclip />
                </Button>
              )}
              {speechToText.isSupported && (
                <Button
                  aria-pressed={speechToText.isListening}
                  disabled={isDisabled}
                  onClick={speechToText.toggle}
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <span className="sr-only">
                    {speechToText.isListening
                      ? t('aiChat.dictationStop')
                      : t('aiChat.dictationStart')}
                  </span>
                  {speechToText.isListening ? <MicOff /> : <Mic />}
                </Button>
              )}
            </div>
            <Button disabled={isDisabled} size="icon" type="submit" variant="secondary">
              <ArrowUp />
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ChatInputArea
